import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  X,
  MessageSquare,
  History,
  Edit,
  Reply,
  Trash2,
  Save,
  XCircle,
  Plus,
  ArrowUp,
  ArrowDown,
  Eye,
  Download,
  Paperclip,
} from "lucide-react";
import { logger } from "../../services/logService";
import { testcaseService } from "../../services/testcaseService";
import { teststepsService } from "../../services/teststepsService";
import {
  getComments,
  addComment,
  deleteComment,
} from "../../services/testCaseCommentService";
import {
  extractBase64Images,
  base64ToFile,
} from "../../services/s3UploadService";
import s3UploadService from "../../services/s3UploadService";
import { getStoredClientId } from "../../utils/tokenUtils";
import sectionService from "../../services/sectionService";
import { projectService } from "../../services/projectService";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import FormSkeleton from "../skeletons/FormSkeleton";
import { Skeleton } from "../common/Skeleton";

const TestCaseDetailPanel = ({
  testCaseId,
  projectId,
  isOpen,
  onClose,
  viewMode: initialViewMode = true,
  onUpdate,
  isViewOnlyMode = false, // Disable editing when viewing baseline test cases
  fromMain = false,
  onPrev = null,
  onNext = null,
  hasPrev = false,
  hasNext = false,
}) => {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testCase, setTestCase] = useState(null);
  const [testSteps, setTestSteps] = useState([]);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form state for editing
  const [formData, setFormData] = useState({
    title: "",
    preconditions: "",
    priority: "medium",
    type_of_test_case: "other",
    automation_status: "not_automated",
    automation_type: null, // Only for BDD: 'record_and_play' or 'self_automated'
    reviewer_id: "",
    assigned_to: "",
    estimate: "",
    references: "",
    testSteps: [],
    attachments: [], // Attachments for editing
  });

  // Dropdown data
  const [sections, setSections] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [projectSettings, setProjectSettings] = useState({
    ai_test_case_generation: false,
    test_case_approval: false,
  });
  const [testCaseTemplateType, setTestCaseTemplateType] =
    useState("MULTIPLE_STEPS"); // SINGLE_STEP, MULTIPLE_STEPS, BDD

  // Rich text editor modules
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      ["link", "image"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "link",
    "list",
    "bullet",
    "image",
  ];

  // Load sections
  const loadSections = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingSections(true);
      const response = await sectionService.getSectionTree(projectId);
      const sectionTree = response?.data?.sections || response?.sections || [];
      setSections(sectionTree);
    } catch (error) {
      logger.error(error, "Failed to load sections");
    } finally {
      setLoadingSections(false);
    }
  }, [projectId]);

  // Load project users
  const loadProjectUsers = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingUsers(true);
      const response = await projectService.getAssignableUsers(projectId);
      setProjectUsers(response?.data?.users || response?.users || []);
    } catch (error) {
      logger.error(error, "Failed to load assignable users");
    } finally {
      setLoadingUsers(false);
    }
  }, [projectId]);

  // Include current user (e.g. chief admin) in Assign To dropdown if not already in list
  const assignableUsersWithCurrent = useMemo(() => {
    const list = projectUsers || [];
    const uid = user?.user_id || user?.id;
    if (!uid) return list;
    const alreadyInList = list.some((u) => (u.user_id || u.id) === uid);
    if (alreadyInList) return list;
    return [
      {
        user_id: uid,
        full_name: user?.full_name || user?.email || "Me",
        email: user?.email || "",
      },
      ...list,
    ];
  }, [user, projectUsers]);

  // Load project settings
  const loadProjectSettings = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await projectService.getProject(projectId);
      const project = response.data?.project || response.data || {};
      setProjectSettings({
        ai_test_case_generation:
          project.ai_test_case_generation === true ||
          project.ai_test_case_generation === "true",
        test_case_approval:
          project.test_case_approval === true ||
          project.test_case_approval === "true",
      });

      // Store test case template type (immutable project setting)
      const templateType = project.test_case_structure_type || "MULTIPLE_STEPS";
      setTestCaseTemplateType(templateType);
    } catch (error) {
      logger.error(error, "Failed to load project settings");
    }
  }, [projectId]);

  // Flatten sections for dropdown
  const flattenedSections = useMemo(() => {
    const flatten = (sections, level = 0) => {
      let result = [];
      sections.forEach((section) => {
        if (section) {
          result.push({
            ...section,
            displayName:
              "  ".repeat(level) +
              (section.name || section.section_name || "Unnamed Section"),
            level,
          });
          if (section.children && section.children.length > 0) {
            result = result.concat(flatten(section.children, level + 1));
          }
        }
      });
      return result;
    };
    return flatten(sections);
  }, [sections]);

  // Load test case data
  const loadTestCase = useCallback(async () => {
    if (!testCaseId || !projectId) return;

    try {
      setLoading(true);

      // Fetch test case
      const response = await testcaseService.getTestCaseById(
        projectId,
        testCaseId,
      );
      let testCaseData = response?.data || response;
      setTestCase(testCaseData);

      // Initialize form data
      setFormData({
        title: testCaseData.title || "",
        preconditions: testCaseData.preconditions || "",
        priority: testCaseData.priority || "medium",
        type_of_test_case: testCaseData.type_of_test_case || "other",
        automation_status: testCaseData.automation_status || "not_automated",
        automation_type: testCaseData.automation_type || null,
        reviewer_id: testCaseData.reviewer_id || "",
        assigned_to: testCaseData.assigned_to || "",
        estimate: testCaseData.estimate || "",
        references: Array.isArray(testCaseData.references)
          ? testCaseData.references.join(", ")
          : testCaseData.references || "",
        section_id: testCaseData.section_id || testCaseData.folder_id || null,
        testSteps: [],
        attachments: testCaseData.attachments || [], // Load existing attachments
      });

      // Fetch test steps
      try {
        const stepsResponse = await teststepsService.getTestSteps(testCaseId);
        let steps = [];
        if (stepsResponse?.data?.test_steps) {
          steps = stepsResponse.data.test_steps;
        } else if (stepsResponse?.test_steps) {
          steps = stepsResponse.test_steps;
        } else if (Array.isArray(stepsResponse?.data)) {
          steps = stepsResponse.data;
        } else if (Array.isArray(stepsResponse)) {
          steps = stepsResponse;
        }
        setTestSteps(steps);

        // Format steps for form
        const formattedSteps =
          steps.length > 0
            ? steps.map((step) => ({
                step: step.action || step.step || "",
                expectedResult:
                  step.expected_result || step.expectedResult || "",
                step_id: step.step_id,
              }))
            : [{ step: "", expectedResult: "" }];

        setFormData((prev) => ({ ...prev, testSteps: formattedSteps }));
      } catch (stepsError) {
        logger.warn(stepsError, "Failed to fetch test steps");
        setTestSteps([]);
        setFormData((prev) => ({
          ...prev,
          testSteps: [{ step: "", expectedResult: "" }],
        }));
      }

      // Load comments after test case is loaded
      loadComments();
    } catch (error) {
      logger.error(error, "Failed to load test case");
      toast.error("Failed to load test case details");
    } finally {
      setLoading(false);
    }
  }, [testCaseId, projectId]);

  // Load comments
  const loadComments = useCallback(async () => {
    if (!testCaseId) return;

    try {
      setLoadingComments(true);
      const response = await getComments(testCaseId);

      // Handle different response structures
      let commentsData = [];

      // Standard success response: { success: true, data: { comments: [...] } }
      if (response?.success && response?.data?.comments) {
        commentsData = response.data.comments;
      }
      // Legacy response structure: { success: true, message: { comments: [...] } }
      else if (
        response?.message?.comments &&
        Array.isArray(response.message.comments)
      ) {
        commentsData = response.message.comments;
      }
      // Direct data structure: { data: { comments: [...] } }
      else if (response?.data?.comments) {
        commentsData = response.data.comments;
      }
      // Direct comments array: { comments: [...] }
      else if (response?.comments) {
        commentsData = response.comments;
      }
      // Response is directly an array
      else if (Array.isArray(response)) {
        commentsData = response;
      }

      console.log("Comments response:", response);
      console.log("Loaded comments:", commentsData);
      setComments(commentsData);
    } catch (error) {
      logger.error(error, "Failed to load comments");
      toast.error("Failed to load comments");
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [testCaseId]);

  // Track if this is the first load for this testCaseId
  const [loadedTestCaseId, setLoadedTestCaseId] = useState(null);
  const [isManualEditMode, setIsManualEditMode] = useState(false);

  // Load test case when panel opens or testCaseId changes
  useEffect(() => {
    if (isOpen && testCaseId) {
      const isNewTestCase = loadedTestCaseId !== testCaseId;

      if (isNewTestCase) {
        // New test case - reset to initial view mode and clear manual edit flag
        setViewMode(initialViewMode);
        setIsManualEditMode(false);
        setLoadedTestCaseId(testCaseId);
      }

      loadTestCase();
    }
  }, [isOpen, testCaseId, initialViewMode, loadTestCase, loadedTestCaseId]);

  // Load sections, users, and project settings when entering edit mode
  useEffect(() => {
    if (!viewMode && isOpen && testCaseId) {
      if (sections.length === 0) {
        loadSections();
      }
      if (projectUsers.length === 0) {
        loadProjectUsers();
      }
      loadProjectSettings();
    }
  }, [
    viewMode,
    isOpen,
    testCaseId,
    loadSections,
    loadProjectUsers,
    loadProjectSettings,
    sections.length,
    projectUsers.length,
  ]);

  // Handle form input change
  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle step change
  const handleStepChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      testSteps: prev.testSteps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step,
      ),
    }));
  };

  // Add step
  const addStep = () => {
    // Prevent adding steps for SINGLE_STEP template
    if (testCaseTemplateType === "SINGLE_STEP") {
      toast.error("SINGLE_STEP template type allows only one step");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      testSteps: [
        ...prev.testSteps,
        {
          step: "",
          expectedResult: testCaseTemplateType === "BDD" ? "" : "",
          gherkin_keyword: testCaseTemplateType === "BDD" ? "Given" : "",
        },
      ],
    }));
  };

  // Remove step
  const removeStep = (index) => {
    // Prevent removing steps for SINGLE_STEP template
    if (testCaseTemplateType === "SINGLE_STEP") {
      toast.error("SINGLE_STEP template type requires exactly one step");
      return;
    }

    if (formData.testSteps.length === 1) {
      toast.error("At least one step is required");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      testSteps: prev.testSteps.filter((_, i) => i !== index),
    }));
  };

  // Move step up
  const moveStepUp = (index) => {
    if (index === 0) return;
    setFormData((prev) => {
      const newSteps = [...prev.testSteps];
      [newSteps[index - 1], newSteps[index]] = [
        newSteps[index],
        newSteps[index - 1],
      ];
      return { ...prev, testSteps: newSteps };
    });
  };

  // Move step down
  const moveStepDown = (index) => {
    setFormData((prev) => {
      if (index === prev.testSteps.length - 1) return prev;
      const newSteps = [...prev.testSteps];
      [newSteps[index], newSteps[index + 1]] = [
        newSteps[index + 1],
        newSteps[index],
      ];
      return { ...prev, testSteps: newSteps };
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!testCaseId || !projectId) return;

    // Validate approver if test_case_approval is enabled
    if (projectSettings.test_case_approval && !formData.reviewer_id) {
      toast.error("Approver is required for this project");
      return;
    }

    // Validate steps based on template type
    if (testCaseTemplateType === "SINGLE_STEP") {
      if (formData.testSteps.length !== 1) {
        toast.error("SINGLE_STEP template requires exactly one step");
        return;
      }
      const step = formData.testSteps[0];
      if (!step.step?.trim()) {
        toast.error("Step description is required");
        return;
      }
      if (!step.expectedResult?.trim()) {
        toast.error("Expected result is required");
        return;
      }
    } else if (testCaseTemplateType === "MULTIPLE_STEPS") {
      if (formData.testSteps.length === 0) {
        toast.error("At least one step is required");
        return;
      }
      for (let i = 0; i < formData.testSteps.length; i++) {
        const step = formData.testSteps[i];
        if (!step.step?.trim()) {
          toast.error(`Step ${i + 1} description is required`);
          return;
        }
        if (!step.expectedResult?.trim()) {
          toast.error(`Step ${i + 1} expected result is required`);
          return;
        }
      }
    } else if (testCaseTemplateType === "BDD") {
      if (formData.testSteps.length === 0) {
        toast.error("At least one Gherkin step is required");
        return;
      }
      let hasGiven = false;
      let hasWhen = false;
      let hasThen = false;

      for (let i = 0; i < formData.testSteps.length; i++) {
        const step = formData.testSteps[i];
        if (!step.step?.trim()) {
          toast.error(`Step ${i + 1} must have content`);
          return;
        }
        if (!step.gherkin_keyword) {
          toast.error(`Step ${i + 1} must have a Gherkin keyword`);
          return;
        }

        const keyword = step.gherkin_keyword;
        if (keyword === "Given" || (keyword === "And" && i === 0))
          hasGiven = true;
        if (keyword === "When" || (keyword === "And" && hasGiven && !hasWhen))
          hasWhen = true;
        if (keyword === "Then" || (keyword === "And" && hasWhen && !hasThen))
          hasThen = true;
      }

      if (!hasGiven) {
        toast.error("BDD template must contain at least one Given clause");
        return;
      }
      if (!hasWhen) {
        toast.error("BDD template must contain at least one When clause");
        return;
      }
      if (!hasThen) {
        toast.error("BDD template must contain at least one Then clause");
        return;
      }
    }

    setSaving(true);
    try {
      const clientId = getStoredClientId();

      // Extract and upload base64 images from preconditions
      const preconditionsImages = extractBase64Images(
        formData.preconditions || "",
      );
      let preconditionsText = formData.preconditions || "";

      for (const img of preconditionsImages) {
        try {
          const file = base64ToFile(img.data, img.fileName, img.mimeType);
          const url = await s3UploadService.uploadFile(
            file,
            "test-cases",
            clientId,
            projectId,
          );
          preconditionsText = preconditionsText.replace(img.fullDataUrl, url);
        } catch (uploadError) {
          logger.warn(uploadError, "Failed to upload image, keeping base64");
        }
      }

      // Process test steps based on template type
      const processedSteps = await Promise.all(
        formData.testSteps.map(async (step) => {
          const stepImages = extractBase64Images(step.step || "");
          const resultImages =
            testCaseTemplateType !== "BDD"
              ? extractBase64Images(step.expectedResult || "")
              : [];

          let stepText = step.step || "";
          let resultText =
            testCaseTemplateType !== "BDD" ? step.expectedResult || "" : "";

          // Upload step images
          for (const img of stepImages) {
            try {
              const file = base64ToFile(img.data, img.fileName, img.mimeType);
              const url = await s3UploadService.uploadFile(
                file,
                "test-steps",
                clientId,
                projectId,
              );
              stepText = stepText.replace(img.fullDataUrl, url);
            } catch (uploadError) {
              logger.warn(
                uploadError,
                "Failed to upload step image, keeping base64",
              );
            }
          }

          // Upload result images (only for non-BDD)
          if (testCaseTemplateType !== "BDD") {
            for (const img of resultImages) {
              try {
                const file = base64ToFile(img.data, img.fileName, img.mimeType);
                const url = await s3UploadService.uploadFile(
                  file,
                  "test-steps",
                  clientId,
                  projectId,
                );
                resultText = resultText.replace(img.fullDataUrl, url);
              } catch (uploadError) {
                logger.warn(
                  uploadError,
                  "Failed to upload result image, keeping base64",
                );
              }
            }
          }

          // For BDD, prepend Gherkin keyword to step text if not already present
          if (testCaseTemplateType === "BDD") {
            const keyword = step.gherkin_keyword || "Given";
            if (!stepText.trim().startsWith(keyword)) {
              stepText = `${keyword} ${stepText.trim()}`;
            }
            // BDD should not have expectedResult field
            resultText = null;
          }

          return {
            step: stepText,
            expectedResult: resultText,
            gherkin_keyword:
              testCaseTemplateType === "BDD"
                ? step.gherkin_keyword || "Given"
                : null,
          };
        }),
      );

      const payload = {
        title: formData.title,
        preconditions: preconditionsText,
        priority: formData.priority,
        type_of_test_case: formData.type_of_test_case,
        automation_status: formData.automation_status,
        automation_type: formData.automation_type || null,
        reviewer_id: formData.reviewer_id || null,
        assigned_to: formData.assigned_to === "" ? null : formData.assigned_to,
        folder_id: formData.section_id,
        estimate: formData.estimate || null,
        references: formData.references
          ? formData.references
              .split(",")
              .map((r) => r.trim())
              .filter(Boolean)
          : [],
        attachments: formData.attachments, // Include attachments in update
        test_case_structure_type: testCaseTemplateType, // Ensure correct template type is sent
        testSteps: processedSteps,
        steps: processedSteps
          .filter((step) => step.step.trim() !== "")
          .map((step, index) => ({
            step_number: index + 1,
            action: step.step,
            expected_result: step.expectedResult || null,
            gherkin_keyword: step.gherkin_keyword || null,
          })),
      };

      await testcaseService.updateTestCase(projectId, testCaseId, payload);
      toast.success("Test case updated successfully");

      // Reload test case data
      await loadTestCase();
      setViewMode(true);
      setIsManualEditMode(false);

      // Notify parent to refresh
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      logger.error(error, "Failed to update test case");
      toast.error(error.message || "Failed to update test case");
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    // Reload original data
    loadTestCase();
    setViewMode(true);
    setIsManualEditMode(false);
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addComment(testCaseId, newComment, null);
      setNewComment("");
      loadComments();
      toast.success("Comment added successfully");
    } catch (error) {
      logger.error(error, "Failed to add comment");
      toast.error("Failed to add comment");
    }
  };

  // Handle reply to comment
  const handleReply = async (parentCommentId) => {
    if (!replyText.trim()) return;

    try {
      await addComment(testCaseId, replyText, parentCommentId);
      setReplyText("");
      setReplyingTo(null);
      loadComments();
      toast.success("Reply added successfully");
    } catch (error) {
      logger.error(error, "Failed to add reply");
      toast.error("Failed to add reply");
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = (attachment) => {
    const url = attachment.file_url || attachment.url;
    const fileName = attachment.file_name || attachment.name || "attachment";

    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started");
    } catch (error) {
      logger.error(error, "Failed to download attachment");
      toast.error("Failed to download attachment");
    }
  };

  // Handle view attachment
  const handleViewAttachment = (attachment) => {
    setPreviewAttachment(attachment);
    setShowPreviewModal(true);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file size (50 MB max)
    const maxSize = 50 * 1024 * 1024; // 50 MB in bytes
    const invalidFiles = files.filter((file) => file.size > maxSize);

    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed 50 MB limit. Max file size: 50 MB`);
      return;
    }

    // Validate total files (10 max)
    const currentAttachments = viewMode
      ? testCase?.attachments || []
      : formData.attachments;
    if (currentAttachments.length + files.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }

    try {
      setUploading(true);
      const clientId = getStoredClientId();

      if (!clientId) {
        toast.error("Client ID is required for file uploads");
        setUploading(false);
        return;
      }

      const uploadedUrls = await s3UploadService.uploadFiles(
        files,
        "test-cases",
        clientId,
        projectId,
      );

      const newAttachments = uploadedUrls.map((url, index) => ({
        url,
        file_url: url,
        name: files[index].name,
        file_name: files[index].name,
        size: files[index].size,
        file_size: files[index].size,
        type: files[index].type,
        file_type: files[index].type,
      }));

      if (viewMode) {
        // If in view mode, update testCase state (for immediate display)
        setTestCase((prev) => ({
          ...prev,
          attachments: [...(prev?.attachments || []), ...newAttachments],
        }));
      } else {
        // If in edit mode, update formData
        setFormData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...newAttachments],
        }));
      }

      toast.success(`${files.length} file(s) uploaded successfully`);

      // Reset file input
      e.target.value = "";
    } catch (error) {
      logger.error(error, "Failed to upload files");
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  // Handle remove attachment
  const handleRemoveAttachment = (index) => {
    if (viewMode) {
      // In view mode, update testCase state
      setTestCase((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
    } else {
      // In edit mode, update formData
      setFormData((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      await deleteComment(commentId);
      loadComments();
      toast.success("Comment deleted successfully");
    } catch (error) {
      logger.error(error, "Failed to delete comment");
      toast.error("Failed to delete comment");
    }
  };

  // Parse preconditions from HTML to bullet points
  const parsePreconditions = (html) => {
    if (!html) return [];
    const text = html.replace(/<[^>]*>/g, "").trim();
    if (!text) return [];
    const items = text
      .split(/\n|•|·/)
      .map((item) => item.trim())
      .filter(Boolean);
    return items.length > 0 ? items : [text];
  };

  if (!isOpen) return null;

  return (
    <div
      className="flex flex-col bg-white border-l border-gray-200"
      style={{
        width: "520px",
        minWidth: "480px",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Left arrow: collapse panel */}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            title="Close panel"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          {viewMode ? (
            <h2 className="text-base font-semibold text-gray-900 truncate flex items-center min-h-[1.25rem]">
              {loading ? (
                <Skeleton className="h-5 w-48 max-w-full" aria-hidden />
              ) : (
                testCase?.title || "Test Case"
              )}
            </h2>
          ) : (
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              className="text-base font-semibold text-gray-900 bg-transparent border-b-2 border-indigo-600 focus:outline-none flex-1 min-w-0"
              placeholder="Test Case Title"
            />
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Prev / Next navigation */}
          {(onPrev || onNext) && (
            <>
              <button
                onClick={onPrev}
                disabled={!hasPrev}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-30"
                title="Previous test case"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
              <button
                onClick={onNext}
                disabled={!hasNext}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-30"
                title="Next test case"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </>
          )}
          {!fromMain && (
            <button
              onClick={() => {
                toast("View History functionality coming soon");
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="View History"
            >
              <History className="w-5 h-5" />
            </button>
          )}
          {viewMode ? (
            !isViewOnlyMode && (
              <button
                onClick={() => {
                  setIsManualEditMode(true);
                  setViewMode(false);
                  // Load sections and users if not already loaded
                  if (sections.length === 0) {
                    loadSections();
                  }
                  if (projectUsers.length === 0) {
                    loadProjectUsers();
                  }
                }}
                className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded"
              >
                <Edit className="w-4 h-4 inline mr-1" />
                Edit
              </button>
            )
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4 inline mr-1" />
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                <XCircle className="w-4 h-4 inline mr-1" />
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="h-full py-2">
            <FormSkeleton fields={9} className="max-w-none mx-0" />
          </div>
        ) : testCase ? (
          <div className="space-y-6">
            {/* Test Case Attributes */}
            <div
              className="rounded-lg pb-4"
              style={{ background: "#F0EEECFF" }}
            >
              <div className="grid grid-cols-3 gap-x-6 gap-y-4 text-sm p-4">
                {/* Row 1: Type | Priority | Approver */}
                <div>
                  <div className="text-gray-500 mb-1">
                    Type {!viewMode && <span className="text-red-500">*</span>}
                  </div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900 capitalize">
                      {testCase.type_of_test_case || "Other"}
                    </div>
                  ) : (
                    <select
                      value={formData.type_of_test_case}
                      onChange={(e) =>
                        handleInputChange("type_of_test_case", e.target.value)
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm"
                    >
                      <option value="functional">Functional</option>
                      <option value="non-functional">Non-Functional</option>
                      <option value="regression">Regression</option>
                      <option value="smoke">Smoke</option>
                      <option value="other">Other</option>
                    </select>
                  )}
                </div>

                <div>
                  <div className="text-gray-500 mb-1">
                    Priority{" "}
                    {!viewMode && <span className="text-red-500">*</span>}
                  </div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900 capitalize">
                      {testCase.priority || "None"}
                    </div>
                  ) : (
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        handleInputChange("priority", e.target.value)
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  )}
                </div>

                <div>
                  <div className="text-gray-500 mb-1">
                    Approver{" "}
                    {!viewMode && projectSettings.test_case_approval && (
                      <span className="text-red-500">*</span>
                    )}
                  </div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {testCase.reviewer_name || "None"}
                    </div>
                  ) : (
                    <select
                      value={formData.reviewer_id}
                      onChange={(e) =>
                        handleInputChange("reviewer_id", e.target.value)
                      }
                      className={`w-full px-2 py-1.5 border ${projectSettings.test_case_approval && !formData.reviewer_id ? "border-red-300" : "border-gray-300"} rounded bg-white text-gray-900 text-sm`}
                    >
                      <option value="">None</option>
                      {projectUsers
                        .filter((u) => {
                          const currentUserId = user?.user_id || user?.id;
                          return u.user_id !== currentUserId;
                        })
                        .map((u) => (
                          <option key={u.user_id} value={u.user_id}>
                            {u.name || u.user_name || u.email}
                          </option>
                        ))}
                    </select>
                  )}
                </div>

                {/* Row 2: Assigned To | Estimate | References */}
                <div>
                  <div className="text-gray-500 mb-1">
                    Assigned To{" "}
                    {!viewMode && <span className="text-red-500">*</span>}
                  </div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {testCase.assigned_to_name || "None"}
                    </div>
                  ) : (
                    <select
                      value={formData.assigned_to || ""}
                      onChange={(e) =>
                        handleInputChange("assigned_to", e.target.value)
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm"
                    >
                      <option value="">Unassigned</option>
                      {assignableUsersWithCurrent.map((u) => (
                        <option
                          key={u.user_id || u.id}
                          value={u.user_id || u.id}
                        >
                          {u.full_name || u.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <div className="text-gray-500 mb-1">Estimate</div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {testCase.estimate || "None"}
                    </div>
                  ) : (
                    <textarea
                      value={formData.estimate || ""}
                      onChange={(e) =>
                        handleInputChange("estimate", e.target.value)
                      }
                      rows={1}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm resize-y"
                    />
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gray-500">References</span>
                    {!viewMode && (
                      <button
                        type="button"
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Add
                      </button>
                    )}
                  </div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {Array.isArray(testCase.references) &&
                      testCase.references.length > 0
                        ? testCase.references.join(", ")
                        : testCase.references || "None"}
                    </div>
                  ) : (
                    <textarea
                      value={
                        Array.isArray(formData.references)
                          ? formData.references.join(", ")
                          : formData.references || ""
                      }
                      onChange={(e) =>
                        handleInputChange("references", e.target.value)
                      }
                      rows={1}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm resize-y"
                      placeholder="e.g. Jira-01, Jira-02"
                    />
                  )}
                </div>

                {/* Row 3: Automation Type | Automation Status | Is Automated */}
                <div>
                  <div className="text-gray-500 mb-1">Automation Type</div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {testCase.automation_type === "record_and_play"
                        ? "Record and Play"
                        : testCase.automation_type === "self_automated"
                          ? "Self Automated"
                          : testCase.automation_type || "None"}
                    </div>
                  ) : (
                    <select
                      value={formData.automation_type || ""}
                      onChange={(e) =>
                        handleInputChange("automation_type", e.target.value)
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm"
                    >
                      <option value="">None</option>
                      <option value="ui">UI</option>
                      <option value="api">API</option>
                      <option value="record_and_play">Record and Play</option>
                      <option value="self_automated">Self Automated</option>
                    </select>
                  )}
                </div>

                <div>
                  <div className="text-gray-500 mb-1">Automation Status</div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {testCase.automation_status === "not_automated"
                        ? "Not Automated"
                        : testCase.automation_status === "automated"
                          ? "Automated"
                          : testCase.automation_status === "not_required"
                            ? "Not Required"
                            : testCase.automation_status || "None"}
                    </div>
                  ) : (
                    <select
                      value={formData.automation_status}
                      onChange={(e) =>
                        handleInputChange("automation_status", e.target.value)
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-gray-900 text-sm"
                    >
                      <option value="not_automated">Not Automated</option>
                      <option value="automated">Automated</option>
                      <option value="not_required">Not Required</option>
                    </select>
                  )}
                </div>

                <div>
                  <div className="text-gray-500 mb-1">Is Automated</div>
                  {viewMode ? (
                    <div className="font-semibold text-gray-900">
                      {testCase.automation_status === "automated"
                        ? "Yes"
                        : "No"}
                    </div>
                  ) : (
                    <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.automation_status === "automated"}
                        onChange={(e) =>
                          handleInputChange(
                            "automation_status",
                            e.target.checked ? "automated" : "not_automated",
                          )
                        }
                        className="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <span>Indicates test automated by script/tool.</span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Preconditions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Preconditions
              </h3>
              {viewMode ? (
                testCase.preconditions ? (
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    {parsePreconditions(testCase.preconditions).map(
                      (item, index) => (
                        <li key={index}>{item}</li>
                      ),
                    )}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No preconditions</p>
                )
              ) : (
                <div className="bg-white rounded-lg border border-gray-200">
                  <ReactQuill
                    value={formData.preconditions}
                    onChange={(value) =>
                      handleInputChange("preconditions", value)
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    className="min-h-[120px]"
                    placeholder="Add preconditions..."
                  />
                </div>
              )}
            </div>

            {/* Steps - Conditionally rendered based on template type */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  {testCaseTemplateType === "BDD" ? "Gherkin Steps" : "Steps"}
                </h3>
                {!viewMode && testCaseTemplateType !== "SINGLE_STEP" && (
                  <button
                    onClick={addStep}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    <Plus className="w-3 h-3" />
                    Add Step
                  </button>
                )}
              </div>
              {viewMode ? (
                testSteps.length > 0 ? (
                  <div className="space-y-4">
                    {testSteps.map((step, index) => (
                      <div
                        key={step.step_id || index}
                        className="border-l-2 border-indigo-200 pl-4"
                      >
                        <div className="text-sm font-medium text-gray-700 mb-1">
                          {testCaseTemplateType === "BDD" &&
                          step.gherkin_keyword ? (
                            <span className="font-semibold text-indigo-600">
                              {step.gherkin_keyword}
                            </span>
                          ) : null}{" "}
                          Step {step.step_number || index + 1}
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          <strong>Step description:</strong>
                          <div
                            className="mt-1"
                            dangerouslySetInnerHTML={{
                              __html: step.action || "",
                            }}
                          />
                        </div>
                        {/* Expected Result - Hidden for BDD, shown for SINGLE_STEP and MULTIPLE_STEPS */}
                        {testCaseTemplateType !== "BDD" &&
                          step.expected_result && (
                            <div className="text-sm text-gray-600">
                              <strong>Expected Result:</strong>
                              <div
                                className="mt-1"
                                dangerouslySetInnerHTML={{
                                  __html: step.expected_result,
                                }}
                              />
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No steps</p>
                )
              ) : (
                <div className="space-y-4">
                  {formData.testSteps.map((step, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-700">
                          Step {index + 1}
                        </span>
                        {/* Only show move/remove buttons for MULTIPLE_STEPS */}
                        {testCaseTemplateType === "MULTIPLE_STEPS" && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => moveStepUp(index)}
                              disabled={index === 0}
                              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveStepDown(index)}
                              disabled={index === formData.testSteps.length - 1}
                              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeStep(index)}
                              disabled={formData.testSteps.length === 1}
                              className="p-1 hover:bg-red-100 text-red-600 rounded disabled:opacity-50"
                              title="Remove step"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Gherkin Keyword Selector for BDD */}
                      {testCaseTemplateType === "BDD" && (
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Gherkin Keyword
                          </label>
                          <select
                            value={step.gherkin_keyword || "Given"}
                            onChange={(e) =>
                              handleStepChange(
                                index,
                                "gherkin_keyword",
                                e.target.value,
                              )
                            }
                            className="w-full px-2 py-1 border border-gray-300 rounded text-gray-900 text-sm"
                          >
                            <option value="Given">Given</option>
                            <option value="When">When</option>
                            <option value="Then">Then</option>
                            <option value="And">And</option>
                            <option value="But">But</option>
                          </select>
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {testCaseTemplateType === "BDD"
                            ? "Gherkin Step"
                            : "Step Details"}
                        </label>
                        <ReactQuill
                          value={step.step}
                          onChange={(value) =>
                            handleStepChange(index, "step", value)
                          }
                          modules={quillModules}
                          formats={quillFormats}
                          className="min-h-[100px]"
                          placeholder={
                            testCaseTemplateType === "BDD"
                              ? "Given the user is logged in..."
                              : "Describe the step..."
                          }
                        />
                      </div>
                      {/* Expected Result - Hidden for BDD, shown for SINGLE_STEP and MULTIPLE_STEPS */}
                      {testCaseTemplateType !== "BDD" && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Expected Result
                          </label>
                          <ReactQuill
                            value={step.expectedResult}
                            onChange={(value) =>
                              handleStepChange(index, "expectedResult", value)
                            }
                            modules={quillModules}
                            formats={quillFormats}
                            className="min-h-[100px]"
                            placeholder="What should happen..."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Attachment
                </h3>
                {!viewMode && !isViewOnlyMode && (
                  <>
                    <input
                      type="file"
                      id="attachment-upload"
                      multiple
                      onChange={handleFileUpload}
                      disabled={
                        uploading ||
                        (viewMode
                          ? testCase?.attachments || []
                          : formData.attachments
                        ).length >= 10
                      }
                      className="hidden"
                    />
                    <label
                      htmlFor="attachment-upload"
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 cursor-pointer transition-colors ${
                        uploading ||
                        (viewMode
                          ? testCase?.attachments || []
                          : formData.attachments
                        ).length >= 10
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      {uploading ? "Uploading..." : "Upload Files"}
                    </label>
                  </>
                )}
              </div>
              {(viewMode ? testCase?.attachments || [] : formData.attachments)
                .length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {(viewMode
                    ? testCase?.attachments || []
                    : formData.attachments
                  ).map((attachment, index) => {
                    const fileUrl = attachment.file_url || attachment.url;
                    const fileName =
                      attachment.file_name || attachment.name || attachment.url;
                    const isImage = attachment.file_name?.match(
                      /\.(jpg|jpeg|png|gif|webp)$/i,
                    );

                    return (
                      <div
                        key={index}
                        className="border border-gray-200 rounded p-2 bg-gray-50 overflow-hidden relative group"
                      >
                        {isImage ? (
                          <div className="relative w-full h-24 flex items-center justify-center">
                            <img
                              src={fileUrl}
                              alt={fileName}
                              className="max-h-full max-w-full object-contain cursor-pointer"
                              onClick={() => handleViewAttachment(attachment)}
                            />
                            {/* Action buttons - always visible at bottom-right */}
                            <div className="absolute bottom-1 right-1 flex items-center gap-1 z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewAttachment(attachment);
                                }}
                                className="p-1.5 bg-white rounded shadow-md hover:bg-gray-100 transition-colors border border-gray-200"
                                title="View attachment"
                              >
                                <Eye className="w-3.5 h-3.5 text-gray-700" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadAttachment(attachment);
                                }}
                                className="p-1.5 bg-white rounded shadow-md hover:bg-gray-100 transition-colors border border-gray-200"
                                title="Download attachment"
                              >
                                <Download className="w-3.5 h-3.5 text-gray-700" />
                              </button>
                              {!viewMode && !isViewOnlyMode && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAttachment(index);
                                  }}
                                  className="p-1.5 bg-white rounded shadow-md hover:bg-red-100 transition-colors border border-gray-200"
                                  title="Remove attachment"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-24 relative">
                            <div className="text-gray-400 text-xs text-center w-full px-2">
                              <Paperclip className="w-6 h-6 mx-auto mb-1" />
                              <div
                                className="truncate w-full max-w-full"
                                title={fileName}
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                }}
                              >
                                {fileName}
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div className="absolute bottom-1 right-1 flex items-center gap-1 z-10">
                              <button
                                onClick={() => handleViewAttachment(attachment)}
                                className="p-1.5 bg-white rounded shadow-md hover:bg-gray-100 transition-colors border border-gray-200"
                                title="View attachment"
                              >
                                <Eye className="w-3.5 h-3.5 text-gray-700" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDownloadAttachment(attachment)
                                }
                                className="p-1.5 bg-white rounded shadow-md hover:bg-gray-100 transition-colors border border-gray-200"
                                title="Download attachment"
                              >
                                <Download className="w-3.5 h-3.5 text-gray-700" />
                              </button>
                              {!viewMode && !isViewOnlyMode && (
                                <button
                                  onClick={() => handleRemoveAttachment(index)}
                                  className="p-1.5 bg-white rounded shadow-md hover:bg-red-100 transition-colors border border-gray-200"
                                  title="Remove attachment"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {!viewMode && !isViewOnlyMode && (
                <>
                  {(viewMode
                    ? testCase?.attachments || []
                    : formData.attachments
                  ).length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      No attachments. Click "Upload Files" to add attachments.
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Max. file size: 50 MB | Max. files: 10
                  </p>
                </>
              )}
            </div>

            {/* Comments */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Comments
              </h3>

              {/* Existing Comments */}
              {loadingComments ? (
                <div className="space-y-3 py-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-[92%] max-w-xl" />
                  <Skeleton className="h-4 w-[66%] max-w-md" />
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-4 mb-4">
                  {comments.map((comment) => {
                    // Handle both backend response format and legacy format
                    const commentId = comment.commentId || comment.comment_id;
                    const commentText =
                      comment.commentText || comment.comment_text;
                    const userName =
                      comment.user?.fullName ||
                      comment.user?.name ||
                      comment.user_name ||
                      comment.author_name ||
                      "Unknown User";
                    const isDeleted = comment.isDeleted || comment.is_deleted;

                    return (
                      <div
                        key={commentId}
                        className="border-b border-gray-100 pb-3"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">
                              {userName}
                            </div>
                            {isDeleted ? (
                              <div className="text-sm text-gray-400 italic mt-1">
                                This comment was deleted
                              </div>
                            ) : (
                              <div
                                className="text-sm text-gray-600 mt-1"
                                dangerouslySetInnerHTML={{
                                  __html: commentText,
                                }}
                              />
                            )}
                          </div>
                          {!isDeleted && !isViewOnlyMode && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  setReplyingTo(
                                    replyingTo === commentId ? null : commentId,
                                  )
                                }
                                className="p-1 text-gray-500 hover:text-gray-700"
                                title="Reply"
                              >
                                <Reply className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteComment(commentId)}
                                className="p-1 text-gray-500 hover:text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Reply Input */}
                        {!isDeleted &&
                          !isViewOnlyMode &&
                          replyingTo === commentId && (
                            <div className="ml-4 mt-2 border-l-2 border-indigo-200 pl-4">
                              <ReactQuill
                                value={replyText}
                                onChange={setReplyText}
                                modules={quillModules}
                                formats={quillFormats}
                                className="mb-2"
                                placeholder="Write a reply..."
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleReply(commentId)}
                                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                >
                                  Reply
                                </button>
                                <button
                                  onClick={() => {
                                    setReplyingTo(null);
                                    setReplyText("");
                                  }}
                                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                        {/* Nested Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="ml-4 mt-2 space-y-2">
                            {comment.replies.map((reply) => {
                              const replyId =
                                reply.commentId || reply.comment_id;
                              const replyText =
                                reply.commentText || reply.comment_text;
                              const replyUserName =
                                reply.user?.fullName ||
                                reply.user?.name ||
                                reply.user_name ||
                                reply.author_name ||
                                "Unknown User";
                              const replyIsDeleted =
                                reply.isDeleted || reply.is_deleted;

                              return (
                                <div
                                  key={replyId}
                                  className="border-l-2 border-gray-200 pl-3"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-medium text-xs text-gray-700">
                                        {replyUserName}
                                      </div>
                                      {replyIsDeleted ? (
                                        <div className="text-xs text-gray-400 italic mt-1">
                                          This reply was deleted
                                        </div>
                                      ) : (
                                        <div
                                          className="text-xs text-gray-600 mt-1"
                                          dangerouslySetInnerHTML={{
                                            __html: replyText,
                                          }}
                                        />
                                      )}
                                    </div>
                                    {!replyIsDeleted && !isViewOnlyMode && (
                                      <button
                                        onClick={() =>
                                          handleDeleteComment(replyId)
                                        }
                                        className="p-1 text-gray-500 hover:text-red-600"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-4 mb-4">
                  No comments yet
                </div>
              )}

              {/* Add Comment */}
              {!isViewOnlyMode && (
                <div className="border-t border-gray-200 pt-4">
                  <ReactQuill
                    value={newComment}
                    onChange={setNewComment}
                    modules={quillModules}
                    formats={quillFormats}
                    className="mb-2"
                    placeholder="Add a comment..."
                  />
                  <button
                    onClick={handleAddComment}
                    className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Add Comment
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No test case data available
          </div>
        )}
      </div>

      {/* Attachment Preview Modal */}
      {showPreviewModal && previewAttachment && (
        <div
          className="fixed inset-0 bg-gray-500/20 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowPreviewModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-5xl max-h-[90vh] w-full mx-4 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Paperclip className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <h3
                  className="text-lg font-semibold text-gray-900 truncate"
                  title={
                    previewAttachment.file_name ||
                    previewAttachment.name ||
                    previewAttachment.url
                  }
                >
                  {previewAttachment.file_name ||
                    previewAttachment.name ||
                    previewAttachment.url}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadAttachment(previewAttachment)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  title="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {(() => {
                const fileUrl =
                  previewAttachment.file_url || previewAttachment.url;
                const fileName =
                  previewAttachment.file_name ||
                  previewAttachment.name ||
                  previewAttachment.url;
                const fileType =
                  previewAttachment.file_type || previewAttachment.type || "";
                const isImage =
                  fileType.startsWith("image/") ||
                  /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName);
                const isPdf =
                  fileType === "application/pdf" || /\.pdf$/i.test(fileName);
                const isVideo =
                  fileType.startsWith("video/") ||
                  /\.(mp4|webm|ogg|mov|avi)$/i.test(fileName);

                if (isImage) {
                  return (
                    <div className="flex items-center justify-center h-full">
                      <img
                        src={fileUrl}
                        alt={fileName}
                        className="max-w-full max-h-[calc(90vh-150px)] object-contain rounded"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div className="hidden flex-col items-center justify-center text-center p-8">
                        <Paperclip className="w-16 h-16 text-gray-400 mb-4" />
                        <p className="text-gray-600 mb-2">
                          Image preview not available
                        </p>
                        <p className="text-sm text-gray-500">{fileName}</p>
                      </div>
                    </div>
                  );
                } else if (isPdf) {
                  return (
                    <div className="w-full h-full">
                      <iframe
                        src={fileUrl}
                        className="w-full h-[calc(90vh-150px)] border rounded"
                        title={fileName}
                        onError={() => {
                          console.error("PDF preview failed");
                        }}
                      />
                    </div>
                  );
                } else if (isVideo) {
                  return (
                    <div className="flex items-center justify-center h-full">
                      <video
                        src={fileUrl}
                        controls
                        className="max-w-full max-h-[calc(90vh-150px)] rounded"
                        onError={(e) => {
                          console.error("Video preview failed");
                        }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                      <Paperclip className="w-16 h-16 text-gray-400 mb-4" />
                      <p className="text-gray-600 mb-4">
                        Preview not available for this file type
                      </p>
                      <p className="text-sm text-gray-500 mb-6">{fileName}</p>
                      <button
                        onClick={() =>
                          handleDownloadAttachment(previewAttachment)
                        }
                        className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                      >
                        Download File
                      </button>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {previewAttachment.file_size && (
                  <span>
                    Size:{" "}
                    {(previewAttachment.file_size / 1024 / 1024).toFixed(2)} MB
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCaseDetailPanel;
